import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';
import {
  nullVatRates,
  vatRates,
} from 'src/app/shared/interfaces/vat-rate.interface';
import {
  eanNumberValidator,
  isUniqueEanNumberValidator,
} from 'src/app/shared/validators/ean-number.validator';
import { knCodeValidator } from 'src/app/shared/validators/kn-code.validator';
import { SubSink } from 'subsink';

import {
  AlcoholicDrinkProperties,
  CustomerSegment,
  NonAlcoholicDrinkProperties,
  SubRetailerAllowance,
} from '@app/api/models';
import { ProductApprovalsActions } from '@app/easylot-admin/modules/product-approvals/store/product-approvals.slice';
import { environment } from '@environments/environment';
import { AbroadCountriesRegions } from '@products/interfaces/abroad-countries-regions.interface';
import { ProductUserType } from '@products/interfaces/product-user-type';
import {
  StatementActions,
  StatementSelectors,
} from '@products/store/statement.slice';
import { EasyLocalizedSeoSubformComponent } from '@shared/components/easy-localized-seo-subform/easy-localized-seo-subform.component';
import { EasyLocalizedValueSubformComponent } from '@shared/components/easy-localized-value-subform/easy-localized-value-subform.component';
import { VatRate } from '@shared/constants/vat-rate';
import {
  convertDecimalToPercent,
  convertPercentToDecimal,
} from '@shared/helpers/decimal-percent.helper';
import {
  getServerErrorForField,
  logInvalidControls,
} from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { DialogService } from '@shared/services/dialog.service';
import { EmbeddableValidator } from '@shared/validators/embeddable.validator';

import {
  StatementDialogComponent,
  StatementDialogDeclined,
} from '../../../shared/statement-dialog/statement-dialog.component';
import {
  AlcoholicDrinkType,
  ContentStatus,
  Country,
  NonAlcoholicDrinkType,
  OtherAlcoholType,
  OtherSpiritType,
  ProductMainCategory,
  SpiritType,
  Unit,
} from '../../interfaces/models';
import { ProductActions, ProductSelectors } from '../../store/products.slice';
import { AlcoholicDrinkPropertiesSubformComponent } from '../alcoholic-drink/alcoholic-drink-properties-subform/alcoholic-drink-properties-subform.component';
import { NonAlcoholicDrinkPropertiesSubformComponent } from '../non-alcoholic-drink/non-alcoholic-drink-properties-subform/non-alcoholic-drink-properties-subform.component';
import { OptionalProductPropertiesSubformComponent } from '../optional-product-properties/optional-product-properties-subform/optional-product-properties-subform.component';
import { ProductDetailedResponse } from './../../../api/models/product-detailed-response';
import { MerchantSelectors } from './../../../merchant/store/merchant.slice';

const ALCOHOL_FREE_VALIDATORS = [
  Validators.min(0),
  Validators.max(0.5),
  Validators.required,
];
const ALCOHOLIC_VALIDATORS = [
  Validators.min(0.6),
  Validators.max(100),
  Validators.required,
];

@Component({
  selector: 'app-product-basic-data',
  templateUrl: './product-basic-data.component.html',
  styleUrls: ['./product-basic-data.component.scss'],
})
export class ProductBasicDataComponent implements OnInit, OnDestroy {
  TITLE = 'PRODUCTS.TITLE.';
  PLACEHOLDER = 'PRODUCTS.PLACEHOLDER.';
  HINT = 'PRODUCTS.HINT.';
  TOOLTIP = 'PRODUCTS.TOOLTIP.';
  REQUIRED: string = this.HINT + 'REQUIRED';

  navigateFrom:
    | 'ADMIN'
    | 'PRODUCT-ADMIN'
    | 'SORTIMENT'
    | 'PRICING'
    | 'PRODUCT-TAKE-OVER'
    | '' = '';

  readOnly = false;
  isEditMode = false;
  isCreateMode = false;
  isAdminMode = false;
  originalData: any;

  originalAlcoholFree = false;

  onDataOriginalDrinkType?: string;

  drinkTypes = [
    {
      groupName: 'SEARCH.SEARCH_BAR.CATEGORY.ALCOHOLIC_DRINK_TYPE',
      options: Object.values(AlcoholicDrinkType).map((alcType) => {
        switch (alcType) {
          case AlcoholicDrinkType.Spirit:
            return {
              groupName: 'SEARCH.SEARCH_BAR.TITLE.SPIRIT_TYPE',
              options: Object.values(SpiritType).map((spiritType) => {
                if (spiritType !== SpiritType.Other) {
                  return {
                    name: 'SEARCH.SEARCH_BAR.SPIRIT_TYPE.' + spiritType,
                    value: spiritType,
                  };
                } else {
                  return {
                    groupName: 'SEARCH.SEARCH_BAR.TITLE.OTHER_SPIRIT_TYPE',
                    options: Object.values(OtherSpiritType).map(
                      (otherSpiritType) => ({
                        name:
                          'SEARCH.SEARCH_BAR.OTHER_SPIRIT_TYPE.' +
                          otherSpiritType,
                        value: otherSpiritType,
                      })
                    ),
                  };
                }
              }),
            };
          case AlcoholicDrinkType.Other:
            return {
              groupName: 'SEARCH.SEARCH_BAR.TITLE.OTHER_ALCOHOL_TYPE',
              options: Object.values(OtherAlcoholType).map((otherAlcType) => ({
                name: 'SEARCH.SEARCH_BAR.OTHER_ALCOHOL_TYPE.' + otherAlcType,
                value: otherAlcType,
              })),
            };
          default:
            return {
              name: 'SEARCH.SEARCH_BAR.ALCOHOLIC_DRINK_TYPE.' + alcType,
              value: alcType,
            };
        }
      }),
    },
    {
      groupName: 'SEARCH.SEARCH_BAR.CATEGORY.NON_ALCOHOLIC_DRINK_TYPE',
      options: Object.values(NonAlcoholicDrinkType).map((type) => ({
        name: 'SEARCH.SEARCH_BAR.NON_ALCOHOLIC_DRINK_TYPE.' + type,
        value: type,
      })),
    },
  ];

  customerSegments: Array<any> = [
    {
      name: 'PRODUCTS.TITLE.MANUFACTURERS_ALLOWED',
      value: 'manufacturersAllowed',
    },
    {
      name: 'PRODUCTS.TITLE.IMPORTERS_ALLOWED',
      value: 'importersAllowed',
    },
    {
      name: 'PRODUCTS.TITLE.WHOLESALERS_ALLOWED',
      value: 'wholesalersAllowed',
    },
    {
      groupName: 'PRODUCTS.TITLE.RETAILERS_ALLOWED',
      value: 'retailersAllowed',
      options: [
        {
          name: 'PRODUCTS.TITLE.HOTELS_ALLOWED',
          value: 'retailersAllowed.hotelsAllowed',
        },
        {
          name: 'PRODUCTS.TITLE.RESTAURANTS_ALLOWED',
          value: 'retailersAllowed.restaurantsAllowed',
        },
        {
          name: 'PRODUCTS.TITLE.CAFES_ALLOWED',
          value: 'retailersAllowed.cafesAllowed',
        },
        {
          name: 'PRODUCTS.TITLE.RETAIL_CHAINS_ALLOWED',
          value: 'retailersAllowed.retailChainsAllowed',
        },
        {
          name: 'PRODUCTS.TITLE.TRAFIKS_ALLOWED',
          value: 'retailersAllowed.trafiksAllowed',
        },
        {
          name: 'PRODUCTS.TITLE.OTHER_RETAILERS__ALLOWED',
          value: 'retailersAllowed.otherRetailersAllowed',
        },
      ],
    },
    {
      name: 'PRODUCTS.TITLE.END_CONSUMERS_ALLOWED',
      value: 'endConsumersAllowed',
    },
  ];

  countries = Country;

  // arrays
  productMainCategories = ProductMainCategory;
  alcoholicDrinkTypes = AlcoholicDrinkType;
  nonAlcoholicDrinkTypes = NonAlcoholicDrinkType;
  units = Unit;
  vatRates?: any;
  isExempt = false;
  contentStatus = ContentStatus;

  error$: Observable<ApiErrorResponse | undefined> = this.store.select(
    ProductSelectors.selectProductSaveError
  );
  isLoading$: Observable<boolean> = this.store.select(
    ProductSelectors.selectEditedProductLoading
  );

  vatExempt$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectVatExempt
  );

  private editedProduct$ = this.store.select(
    ProductSelectors.selectEditedProduct
  );

  private statement$ = this.store.select(StatementSelectors.selectStatement);
  private statementId!: string;

  private subs = new SubSink();

  userType?: ProductUserType;

  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];

  get devMode(): boolean {
    return environment.dev;
  }

  form = this.fb.group({
    id: this.fb.control(null), // hidden
    secondaryProductSheet: this.fb.control(false), // hidden
    name: ['', Validators.required],
    brand: ['', Validators.required],
    vatRate: [null, Validators.required],
    productMainCategory: this.fb.control('DRINKS'),
    drinkType: this.fb.control(null, Validators.required),
    alcoholicDrinkType: this.fb.control(null),
    nonAlcoholicDrinkType: this.fb.control(null),
    eanCodes: this.fb.array([], [Validators.required]),
    packagingUnit: this.fb.group({
      id: this.fb.control(''), // hidden
      withDepositFee: this.fb.control(false),
      mainType: this.fb.control('STANDALONE'),
      packingMethodSubType: this.fb.control('', Validators.required),
      refId: this.fb.control(''),
      refName: this.fb.control(''),
      amount: this.fb.control(null, [
        Validators.required,
        Validators.min(0.00001),
      ]),
      unit: this.fb.control('', Validators.required),
      statementId: this.fb.control(''), // hidden
      unique: this.fb.control(true), // hidden
    }),
    equalizedAmount: false,
    grossWeightInKilograms: [0, [Validators.required, Validators.min(0.00001)]],
    packagingFreeProduct: false,
    customerSegment: this.fb.group({
      manufacturersAllowed: this.fb.control(false),
      importersAllowed: this.fb.control(false),
      wholesalersAllowed: this.fb.control(false),
      retailers: this.fb.group({
        hotelsAllowed: this.fb.control(false),
        restaurantsAllowed: this.fb.control(false),
        cafesAllowed: this.fb.control(false),
        retailChainsAllowed: this.fb.control(false),
        trafiksAllowed: this.fb.control(false),
        otherRetailersAllowed: this.fb.control(false),
        hotelsAllowedModifiable: this.fb.control(true),
        restaurantsAllowedModifiable: this.fb.control(true),
        cafesAllowedModifiable: this.fb.control(true),
        retailChainsAllowedModifiable: this.fb.control(true),
        trafiksAllowedModifiable: this.fb.control(true),
        otherRetailersAllowedModifiable: this.fb.control(true),
      }),
      retailersAllowed: this.fb.control(false),
      retailersAllowedModifiable: this.fb.control(true),
      endConsumersAllowed: this.fb.control(false),
      endConsumersAllowedModifiable: this.fb.control(true),
      importersAllowedModifiable: this.fb.control(true),
      manufacturersAllowedModifiable: this.fb.control(true),
      wholesalersAllowedModifiable: this.fb.control(true),
    }),
    customerSegmentSelector: this.fb.control(null, Validators.required),
    knCode: ['', [Validators.required, knCodeValidator]],
    allergenic: this.fb.group({
      gluten: false,
      shellfish: false,
      eggs: false,
      fish: false,
      peanuts: false,
      soyabeans: false,
      milk: false,
      treeNuts: false,
      celery: false,
      mustard: false,
      sesameSeeds: false,
      sulphurDioxidAndSulphits: false,
      lupin: false,
      molluscs: false,
    }),
    placeOfOrigin: ['', Validators.required],
    shortDescription: EasyLocalizedValueSubformComponent.buildDefaultForm(),
    detailedDescription: EasyLocalizedValueSubformComponent.buildDefaultForm(),
    videoLink: this.buildVideoLinkControl(),
    optionalProperties: OptionalProductPropertiesSubformComponent.buildForm(
      this.fb
    ),
    robotsMetaTags: new UntypedFormControl(null),
    seoProperties: new UntypedFormGroup({}),

    productImages: this.fb.array([]),
    merchantId: this.fb.control(null), // hidden
    status: this.fb.control(''), // hidden
  });

  metaInput = new UntypedFormControl('');
  metaTags: string[] = [];

  selectedSegments: Array<any> = [];

  constructor(
    private readonly store: Store,
    private readonly fb: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    readonly router: Router,
    private changeDetector: ChangeDetectorRef,
    private readonly embeddableValidator: EmbeddableValidator,
    private readonly dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.subs.sink = combineLatest([
      this.route.queryParams,
      this.route.parent!.data,
    ]).subscribe(([params, data]) => {
      this.handleRoutingData(data, params);

      if (!this.isAdminMode && !this.readOnly) {
        this.subs.sink = this.statement$.subscribe((statement) => {
          if (!statement) {
            this.store.dispatch(StatementActions.loadStatement.trigger());
          } else {
            this.statementId = statement.actualStatementId ?? '';
          }
        });
      }

      if (params['id']) {
        this.store.dispatch(
          ProductActions.loadEditedProduct.trigger({
            packagingUnitId: params['id'],
            editable: !this.readOnly,
          })
        );
      } else if (params['userType']) {
        this.userType = params['userType'];
        this.setSecondaryProductSheet(params['userType'] === 'OTHER');
      }
    });

    this.subs.sink = this.editedProduct$.subscribe((product) => {
      if (product?.id) {
        this.onData(product);
        this.originalData = product;
      } else {
        this.getArrayCtrl('eanCodes').push(
          new UntypedFormGroup({
            ean: new UntypedFormControl(null, [
              Validators.required,
              eanNumberValidator,
              isUniqueEanNumberValidator,
            ]),
          })
        );
      }
    });

    this.subs.add(
      this.vatExempt$.subscribe((exempt) => {
        this.isExempt = exempt;
        if (this.isCreateMode || this.isEditMode) {
          this.vatRates = this.isExempt ? nullVatRates : vatRates;
          if (this.isCreateMode) {
            this.form.controls['vatRate'].setValue(
              this.isExempt ? VatRate.Zero : VatRate.TwentySeven
            );
          }
        }
      })
    );
  }

  handleRoutingData(data: any, params: any): void {
    this.navigateFrom = data['navigateFrom'];
    this.readOnly = !!data['readOnly'];
    this.isEditMode =
      !!data['isEditMode'] || (!!params['id'] && !this.readOnly);
    this.isAdminMode = data['navigateFrom'] === 'ADMIN';
    this.isCreateMode = !this.isEditMode && !this.isAdminMode && !this.readOnly;
  }

  buildAlcoholicDrinkPropertiesSubform(
    type?: AlcoholicDrinkType,
    data?: AlcoholicDrinkProperties
  ) {
    if (type) {
      this.form.removeControl('alcoholicDrinkProperties');
      this.changeDetector.detectChanges();
      this.form.setControl(
        'alcoholicDrinkProperties',
        AlcoholicDrinkPropertiesSubformComponent.buildForm(
          this.fb,
          type,
          data,
          this.isSecondaryProductSheet
        )
      );

      this.subs.sink = this.form
        .get('alcoholicDrinkProperties')
        ?.get('grapeWineProperties')
        ?.get('wineLandscapeUnitAbroad')
        ?.valueChanges.subscribe((change) => {
          if (this.form.touched) {
            this.setValuePlaceOfOrigin(change, null);
          }
        });

      this.subs.sink = this.form
        .get('alcoholicDrinkProperties')
        ?.get('grapeWineProperties')
        ?.get('wineLandscapeUnitHun')
        ?.valueChanges.subscribe((change) => {
          if (this.form.touched) {
            this.setValuePlaceOfOrigin(null, change);
          }
        });

      if (this.readOnly) {
        this.form.get('alcoholicDrinkProperties')?.disable();
      }
    } else {
      this.form.removeControl('alcoholicDrinkProperties');
    }
  }

  setValuePlaceOfOrigin(regionAbroad: string | null, regionHun: string | null) {
    let country!: string;
    if (regionAbroad) {
      for (const [key, value] of Object.entries(AbroadCountriesRegions)) {
        if (value.includes(regionAbroad)) {
          country = key;
          continue;
        }
      }
    } else if (regionHun) {
      country = 'HUNGARY';
    }
    if (country) {
      this.form.get('placeOfOrigin')?.setValue(country);
      this.form.get('placeOfOrigin')?.disable();
    } else {
      this.form.get('placeOfOrigin')?.reset();
      this.form.get('placeOfOrigin')?.enable();
    }
  }

  buildNonAlcoholicDrinkPropertiesSubform(
    type?: NonAlcoholicDrinkType,
    data?: NonAlcoholicDrinkProperties
  ) {
    if (type) {
      this.form.removeControl('nonAlcoholicDrinkProperties');
      this.changeDetector.detectChanges();
      this.form.setControl(
        'nonAlcoholicDrinkProperties',
        NonAlcoholicDrinkPropertiesSubformComponent.buildForm(
          this.fb,
          type,
          data,
          this.isSecondaryProductSheet
        )
      );
      if (this.readOnly) {
        this.form.get('nonAlcoholicDrinkProperties')?.disable();
      }
    } else {
      this.form.removeControl('nonAlcoholicDrinkProperties');
    }
  }

  private buildVideoLinkControl() {
    return this.fb.control(null, [], [this.embeddableValidator.isEmbeddable]);
  }

  onAlcoholicDrinkTypeSelected(
    type?: AlcoholicDrinkType,
    data?: AlcoholicDrinkProperties
  ) {
    setTimeout(() => {
      if (this.form.get('alcoholicDrinkProperties')) {
        // if the subform already exists, remove it
        this.form.removeControl('alcoholicDrinkProperties');
        // update the DOM to remove any previously added subform components, forcing it to later create new ones
        this.changeDetector.detectChanges();
      }

      if (this.form.get('nonAlcoholicDrinkProperties')) {
        this.form.removeControl('nonAlcoholicDrinkProperties');

        this.changeDetector.detectChanges();
      }
      // build
      this.buildAlcoholicDrinkPropertiesSubform(type, data);
      this.form.get('placeOfOrigin')?.reset();
      this.form.get('placeOfOrigin')?.enable();
    });
  }

  onNonAlcoholicDrinkTypeSelected(type?: NonAlcoholicDrinkType) {
    setTimeout(() => {
      if (this.form.touched) {
        if (this.form.get('nonAlcoholicDrinkProperties')) {
          this.form.removeControl('nonAlcoholicDrinkProperties');

          this.changeDetector.detectChanges();
        }

        if (this.form.get('alcoholicDrinkProperties')) {
          this.form.removeControl('alcoholicDrinkProperties');

          this.changeDetector.detectChanges();
        }

        this.buildNonAlcoholicDrinkPropertiesSubform(type);
      }
    });
  }

  onData(data: ProductDetailedResponse) {
    this.form.reset();
    data = convertFractions(data, 'GET');

    if (!this.form.get('customerSegmentSelector')) {
      this.form.addControl(
        'customerSegmentSelector',
        this.fb.control(null, Validators.required)
      );
    }

    this.setSecondaryProductSheet(data.secondaryProductSheet);

    if (data.placeOfOrigin) {
      this.form.controls['placeOfOrigin'].setValue(data.placeOfOrigin);
    }

    const retailers = data.customerSegment?.retailers;

    if (
      retailers &&
      Object.keys(retailers).some(
        (key: any) =>
          !!retailers[key as keyof SubRetailerAllowance] &&
          [
            'hotelsAllowed',
            'restaurantsAllowed',
            'cafesAllowed',
            'retailChainsAllowed',
            'trafiksAllowed',
            'otherRetailersAllowed',
          ].includes(key)
      )
    ) {
      this.form.get('customerSegment.retailersAllowed')?.setValue(true);
    }

    if (
      retailers &&
      Object.keys(retailers)
        .filter((key: string) => key.includes('Modifiable'))
        .some(
          (key: any) => retailers[key as keyof SubRetailerAllowance] === false
        )
    ) {
      this.form
        .get('customerSegment.retailersAllowedModifiable')
        ?.setValue(false);
    } else {
      this.form
        .get('customerSegment.retailersAllowedModifiable')
        ?.setValue(true);
    }

    this.customerSegments = this.customerSegments.map((segment) => {
      if (segment.value === 'retailersAllowed') {
        return {
          ...segment,
          options: segment.options.map((retailerSegment: any) => ({
            ...retailerSegment,
            disabled:
              !data.customerSegment?.retailers?.[
                (retailerSegment.value.split('.')[1] +
                  'Modifiable') as keyof SubRetailerAllowance
              ],
          })),
          disabled: this.isAllRetailerSegmentDisabled(
            data.customerSegment?.retailers
          ),
        };
      } else {
        return {
          ...segment,
          disabled:
            !data.customerSegment?.[
              (segment.value + 'Modifiable') as keyof CustomerSegment
            ],
        };
      }
    });

    this.selectedSegments = [
      ...Object.keys(data.customerSegment).filter(
        (segment) =>
          !segment.includes('Modifiable') &&
          segment !== 'retailers' &&
          data.customerSegment[segment as keyof CustomerSegment]
      ),
      ...Object.keys(data.customerSegment.retailers ?? {})
        .filter(
          (retailerSegment) =>
            !retailerSegment.includes('Modifiable') &&
            data.customerSegment.retailers?.[
              retailerSegment as keyof SubRetailerAllowance
            ]
        )
        .map(
          (filteredRetailerSegment) =>
            'retailersAllowed.' + filteredRetailerSegment
        ),
    ];

    this.form.controls['customerSegmentSelector'].setValue(
      this.selectedSegments
    );

    if (data.seoProperties) {
      this.form.setControl(
        'seoProperties',
        EasyLocalizedSeoSubformComponent.buildForm(data.seoProperties)
      );
      if (this.readOnly) {
        this.form.get('seoProperties')?.disable();
      }
    }
    if (data.robotsMetaTags) {
      this.metaTags = [...data.robotsMetaTags];
    }

    if (data.rootPackagingUnit) {
      this.form.addControl(
        'rootPackagingUnit',
        this.fb.group({
          id: this.fb.control(''),
          withDepositFee: this.fb.control(false),
          mainType: this.fb.control(null),
          packingMethodSubType: this.fb.control('', Validators.required),
          refId: this.fb.control(''),
          amount: this.fb.control(null, [
            Validators.required,
            Validators.min(1),
          ]),
          unit: this.fb.control('', Validators.required),
          statementId: this.fb.control(''),
          unique: this.fb.control(true),
        })
      );
      this.form.get('rootPackagingUnit')?.disable();
    }

    this.form
      .get('packagingUnit')
      ?.get('amount')
      ?.setValidators(
        data.packagingUnit?.mainType === 'STANDALONE'
          ? [Validators.required, Validators.min(0.00001)]
          : [Validators.required, Validators.min(1)]
      );

    this.onDataOriginalDrinkType = data.alcoholicDrinkType
      ? this.getAlcoholicDrinkType(data)
      : data.nonAlcoholicDrinkType;

    this.form.patchValue({
      ...data,
      drinkType: this.onDataOriginalDrinkType,
      productMainCategory: ProductMainCategory.Drinks,
    });

    if (data.eanCodes) {
      this.getArrayCtrl('eanCodes').clear();

      data.eanCodes.forEach((ean) => {
        this.getArrayCtrl('eanCodes').push(
          new UntypedFormGroup({
            ean: new UntypedFormControl(ean, [
              Validators.required,
              eanNumberValidator,
              isUniqueEanNumberValidator,
            ]),
          })
        );
      });
    }

    this.buildAlcoholicDrinkPropertiesSubform(
      data.alcoholicDrinkType,
      data.alcoholicDrinkProperties
    );

    this.buildNonAlcoholicDrinkPropertiesSubform(
      data.nonAlcoholicDrinkType,
      data.nonAlcoholicDrinkProperties
    );

    if (!(this.isCreateMode || this.isEditMode)) {
      this.vatRates = data.vatRate === 'ZERO' ? nullVatRates : vatRates;
    } else {
      if (!this.vatRates?.includes(data.vatRate)) {
        this.form.get('vatRate')?.setValue(null);
      }
    }

    if (this.readOnly) {
      this.form.disable();
      this.metaInput.disable();
    }
  }

  private checkSpecialOtherType(): void {
    if (this.getCtrl('specialTypeOther')?.value) {
      this.getCtrl('specialType').setValue([
        ...this.getCtrl('specialType').value,
        this.getCtrl('specialTypeOther').value,
      ]);
    }
  }

  getAlcoholicDrinkType(data: ProductDetailedResponse) {
    switch (data.alcoholicDrinkType) {
      case 'OTHER': {
        return data.alcoholicDrinkProperties?.otherAlcoholProperties
          ?.otherAlcoholType;
      }
      case 'SPIRIT': {
        return data.alcoholicDrinkProperties?.spiritProperties?.spiritType ===
          'OTHER'
          ? data.alcoholicDrinkProperties?.spiritProperties
              ?.otherSpiritProperties?.type
          : data.alcoholicDrinkProperties?.spiritProperties?.spiritType;
      }
      default: {
        return data.alcoholicDrinkType;
      }
    }
  }

  valueChanged(selectedCustomerSegments?: Array<string>) {
    if (
      selectedCustomerSegments
        ?.filter((element) => !this.selectedSegments.includes(element))
        .concat(
          this.selectedSegments.filter(
            (element) => !selectedCustomerSegments.includes(element)
          )
        )
    ) {
      this.selectedSegments = selectedCustomerSegments;
      Object.keys(this.getGroupCtrl('customerSegment').controls).forEach(
        (segment) => {
          if (
            !segment.includes('Modifiable') &&
            !segment.includes('retailers')
          ) {
            this.getGroupCtrl('customerSegment').controls[segment]?.setValue(
              selectedCustomerSegments.includes(segment)
            );
          }
        }
      );

      let retailerGroup = (this.getGroupCtrl('customerSegment') as FormGroup)
        ?.controls['retailers'];
      Object.keys((retailerGroup as FormGroup).controls).forEach(
        (retailerSegment) => {
          if (!retailerSegment.includes('Modifiable')) {
            (retailerGroup as FormGroup).controls[retailerSegment]?.setValue(
              selectedCustomerSegments.includes(
                'retailersAllowed.' + retailerSegment
              )
            );
          }
        }
      );
    }
  }

  isAllRetailerSegmentDisabled(retailers?: SubRetailerAllowance) {
    return Object.keys(retailers ?? {})
      ?.filter((retailer) => retailer.includes('Modifiable'))
      .every(
        (filteredRetailer) =>
          !retailers?.[filteredRetailer as keyof SubRetailerAllowance]
      );
  }

  drinkTypeChanged(selectedDrinkType: string) {
    if (
      selectedDrinkType &&
      selectedDrinkType !== this.onDataOriginalDrinkType
    ) {
      if (
        Object.values(this.nonAlcoholicDrinkTypes).includes(
          selectedDrinkType as NonAlcoholicDrinkType
        )
      ) {
        this.onNonAlcoholicDrinkTypeSelected(
          selectedDrinkType as NonAlcoholicDrinkType
        );
        this.form.controls['nonAlcoholicDrinkType'].setValue(selectedDrinkType);
        this.form.controls['alcoholicDrinkType'].setValue(null);
      } else {
        if (
          Object.values(SpiritType).includes(selectedDrinkType as SpiritType)
        ) {
          this.onAlcoholicDrinkTypeSelected('SPIRIT', {
            spiritProperties: { spiritType: selectedDrinkType as SpiritType },
            alcoholLevel: 0.6,
          });
          this.form.controls['alcoholicDrinkType'].setValue('SPIRIT');

          if (selectedDrinkType === 'OTHER') {
            this.onAlcoholicDrinkTypeSelected('SPIRIT', {
              spiritProperties: {
                spiritType: 'OTHER',
                otherSpiritProperties: {
                  type: selectedDrinkType as any,
                  distilled: false,
                },
              },
              alcoholLevel: 0.6,
            });
            this.form.controls['alcoholicDrinkType'].setValue('SPIRIT');
          }
        } else if (
          Object.values(OtherSpiritType).includes(
            selectedDrinkType as OtherSpiritType
          )
        ) {
          this.onAlcoholicDrinkTypeSelected('SPIRIT', {
            spiritProperties: {
              spiritType: 'OTHER',
              otherSpiritProperties: {
                type: selectedDrinkType as any,
                distilled: false,
              },
            },
            alcoholLevel: 0.6,
          });
          this.form.controls['alcoholicDrinkType'].setValue('SPIRIT');
        } else if (
          Object.values(OtherAlcoholType).includes(
            selectedDrinkType as OtherAlcoholType
          )
        ) {
          this.onAlcoholicDrinkTypeSelected('OTHER', {
            otherAlcoholProperties: {
              otherAlcoholType: selectedDrinkType as any,
            },
            alcoholLevel: 0.6,
          });
          this.form.controls['alcoholicDrinkType'].setValue('OTHER');
        } else {
          this.onAlcoholicDrinkTypeSelected(
            selectedDrinkType as AlcoholicDrinkType
          );
          this.form.controls['alcoholicDrinkType'].setValue(selectedDrinkType);
        }
        this.form.controls['nonAlcoholicDrinkType'].setValue(null);
      }
    }
  }

  onAlcoholFreeChanged(isAlcoholFree: boolean) {
    if (this.originalAlcoholFree !== isAlcoholFree) {
      this.originalAlcoholFree = isAlcoholFree;
      if (isAlcoholFree) {
        this.form.get('alcoholicDrinkProperties.alcoholFree')?.setValue(true);
        this.form.get('alcoholicDrinkProperties.alcoholLevel')?.setValue(0);
        this.form
          .get('alcoholicDrinkProperties.alcoholLevel')
          ?.setValidators(ALCOHOL_FREE_VALIDATORS);
        this.form
          .get('alcoholicDrinkProperties.alcoholLevel')
          ?.updateValueAndValidity();
      } else {
        this.form.get('alcoholicDrinkProperties.alcoholFree')?.setValue(false);
        this.form.get('alcoholicDrinkProperties.alcoholLevel')?.setValue(0.6);
        this.form
          .get('alcoholicDrinkProperties.alcoholLevel')
          ?.setValidators(ALCOHOLIC_VALIDATORS);
        this.form
          .get('alcoholicDrinkProperties.alcoholLevel')
          ?.updateValueAndValidity();
      }
    }
  }

  onSubmit() {
    this.checkSpecialOtherType();

    if (this.isSecondaryProductSheet) {
      this.form.removeControl('customerSegmentSelector');
    }

    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.form.removeControl('customerSegmentSelector');
      let formValue = this.form.value;
      if (this.form.get('rootPackagingUnit')) {
        formValue = {
          ...formValue,
          rootPackagingUnit: { ...this.form.get('rootPackagingUnit')?.value },
        };
      }
      formValue = trimInput(formValue);
      // placeOfOrigin control can be disabled
      if (!formValue.placeOfOrigin && this.form.get('placeOfOrigin')?.value) {
        formValue.placeOfOrigin = this.form.get('placeOfOrigin')?.value;
      }

      // TODO kiszedni, ha a BE javította a validátorát
      // some of customer segment controls can be disabled
      formValue.customerSegment.endConsumersAllowed = this.form.get(
        'customerSegment.endConsumersAllowed'
      )?.value;
      formValue.customerSegment.importersAllowed = this.form.get(
        'customerSegment.importersAllowed'
      )?.value;
      formValue.customerSegment.manufacturersAllowed = this.form.get(
        'customerSegment.manufacturersAllowed'
      )?.value;
      formValue.customerSegment.wholesalersAllowed = this.form.get(
        'customerSegment.wholesalersAllowed'
      )?.value;
      formValue.customerSegment.retailers.cafesAllowed = this.form.get(
        'customerSegment.retailers.cafesAllowed'
      )?.value;
      formValue.customerSegment.retailers.hotelsAllowed = this.form.get(
        'customerSegment.retailers.hotelsAllowed'
      )?.value;
      formValue.customerSegment.retailers.otherRetailersAllowed = this.form.get(
        'customerSegment.retailers.otherRetailersAllowed'
      )?.value;
      formValue.customerSegment.retailers.restaurantsAllowed = this.form.get(
        'customerSegment.retailers.restaurantsAllowed'
      )?.value;
      formValue.customerSegment.retailers.retailChainsAllowed = this.form.get(
        'customerSegment.retailers.retailChainsAllowed'
      )?.value;
      formValue.customerSegment.retailers.trafiksAllowed = this.form.get(
        'customerSegment.retailers.trafiksAllowed'
      )?.value;

      formValue.eanCodes = this.getArrayCtrl('eanCodes').controls.map(
        (eanGroup) => {
          return (eanGroup as UntypedFormGroup).controls['ean'].value;
        }
      );

      const data = convertFractions(formValue, 'SUBMIT');
      if (
        !(
          data.rootPackagingUnit?.statementId || data.packagingUnit?.statementId
        ) &&
        data.secondaryProductSheet !== true &&
        !this.isAdminMode
      ) {
        this.subs.sink = this.showStatement().subscribe((result) => {
          if (!result) {
            // statement dismissed
            return;
          }
          if (result === 'decline') {
            // statement declined
            this.subs.sink =
              this.showSaveSecondaryProductSheetWarning().subscribe(
                (result) => {
                  if (!result) return; // warning dismissed, stay on the form
                  // warning accepted, save as secondary product sheet
                  this.store.dispatch(
                    ProductActions.saveProduct.trigger({
                      product: asSecondaryProductSheet(data),
                    })
                  );
                }
              );
          } else {
            // statement accepted
            this.store.dispatch(
              ProductActions.saveProduct.trigger({
                product: {
                  ...data,
                  packagingUnit: {
                    ...data.packagingUnit!,
                    statementId: this.statementId,
                  },
                },
              })
            );
          }
        });
      } else {
        this.store.dispatch(
          ProductActions.saveProduct.trigger({
            product: data,
            isAdminMode: this.isAdminMode,
          })
        );
      }
    } else {
      logInvalidControls(this.form);
    }
  }

  /**
   * if accepted,  returns statementId,
   * if declined,  returns 'decline'
   * if dismissed, returns undefined
   */
  showStatement(): Observable<number | StatementDialogDeclined | undefined> {
    const dialogConfig: MatDialogConfig = {
      data: {
        isTakeOver: false,
      },
    };
    return this.dialogService
      .openDialog(StatementDialogComponent, dialogConfig)
      .afterClosed();
  }

  showSaveSecondaryProductSheetWarning(): Observable<boolean> {
    return this.dialogService.open({
      type: 'CONFIRM',
      title: 'DIALOG.TITLE.SAVE_AS_SECONDARY_PRODUCT_SHEET',
      content: 'DIALOG.MSG.SAVE_AS_SECONDARY_PRODUCT_SHEET',
    });
  }

  onCancel() {
    const isSelling = this.router.url.includes('selling');
    if (!this.isAdminMode) {
      switch (this.navigateFrom) {
        case 'SORTIMENT':
          this.router.navigate([
            isSelling ? '/selling' : '/buying',
            'sortiment',
            'products',
          ]);
          break;
        case 'PRODUCT-TAKE-OVER':
          this.router.navigate(['/admin/approvals/product-take-over']);
          break;
        case 'PRODUCT-ADMIN':
          this.router.navigate(['/admin/products']);
          break;
        default: {
          switch (this.form.get('status')?.value ?? '') {
            case 'DRAFT':
              this.router.navigate([
                '/selling/sortiment/product-sortiment/products-start/products/pending',
              ]);
              break;
            case 'PUBLISHED':
              this.router.navigate([
                '/selling/sortiment/product-sortiment/products-start/products',
              ]);
              break;
            case 'REJECTED':
              this.router.navigate([
                '/selling/sortiment/product-sortiment/products-start/products/rejected',
              ]);
              break;
            default:
              this.router.navigate([
                '/selling/sortiment/product-sortiment/products-start/products/pending',
              ]);
              break;
          }
        }
      }
    } else {
      if (this.isEditMode) {
        this.router.navigate(['/admin/approvals/products/view'], {
          queryParams: { id: this.form.value.packagingUnit.id },
        });
      } else {
        this.router.navigate(['/admin/approvals/products']);
      }
    }
  }

  setSecondaryProductSheet(secondary: boolean) {
    this.getCtrl('secondaryProductSheet').setValue(secondary);
    if (secondary) {
      this.form.removeControl('detailedDescription');
      this.form.removeControl('videoLink');
    } else {
      if (!this.getCtrl('detailedDescription')) {
        this.form.setControl(
          'detailedDescription',
          EasyLocalizedValueSubformComponent.buildDefaultForm()
        );
      }
      if (!this.getCtrl('videoLink')) {
        this.form.setControl('videoLink', this.buildVideoLinkControl());
      }
      this.changeDetector.detectChanges();
    }
  }

  get isSecondaryProductSheet() {
    return !!this.getCtrl('secondaryProductSheet')?.value;
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  getCtrl(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }

  getGroupCtrl(controlName: string): UntypedFormGroup {
    return this.form.get(controlName) as UntypedFormGroup;
  }

  getArrayCtrl(controlName: string): UntypedFormArray {
    return this.form.get(controlName) as UntypedFormArray;
  }

  asGroup(subform: AbstractControl): UntypedFormGroup {
    return subform as UntypedFormGroup;
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.metaTags.push(value);
    }

    event.chipInput.clear();
    this.metaInput.setValue(null);

    this.form.patchValue({ robotsMetaTags: this.metaTags });
    this.form.markAsTouched();
  }

  removeTag(tag: string): void {
    const index = this.metaTags.indexOf(tag);

    if (index >= 0) {
      this.metaTags.splice(index, 1);
    }

    this.form.patchValue({ robotsMetaTags: this.metaTags });
  }

  public changeProductStatus(newStatus: ContentStatus) {
    this.store.dispatch(
      ProductApprovalsActions.updateProductStatus.trigger({
        updateRequest: {
          uuidList: [this.form.get('packagingUnit.id')?.value],
          newStatus,
        },
      })
    );
  }

  generateRandomLengthNumber(
    length: number,
    controlName: string,
    index?: number
  ): void {
    const max = Math.pow(10, length) - 1;
    const min = Math.pow(10, length - 1);
    if (index !== undefined && index >= 0) {
      (this.getArrayCtrl(controlName).at(index!) as UntypedFormGroup).controls[
        'ean'
      ]?.setValue(Math.floor(Math.random() * (max - min + 1) + min));
    } else {
      this.form.controls[controlName].patchValue(
        Math.floor(Math.random() * (max - min + 1) + min)
      );
    }
  }

  addEanField() {
    this.getArrayCtrl('eanCodes').push(
      new UntypedFormGroup({
        ean: new UntypedFormControl(null, [
          Validators.required,
          eanNumberValidator,
          isUniqueEanNumberValidator,
        ]),
      })
    );
  }

  removeEanField(index: number) {
    this.getArrayCtrl('eanCodes').removeAt(index);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

/// multiply all fractions by 100 when getting data
/// divide all fractions by 100 when submitting data
function convertFractions(
  data: ProductDetailedResponse,
  direction: 'GET' | 'SUBMIT'
): ProductDetailedResponse {
  let result = structuredClone(data);

  if (direction == 'GET') {
    if (result?.alcoholicDrinkProperties?.alcoholLevel) {
      result.alcoholicDrinkProperties.alcoholLevel = convertDecimalToPercent(
        result.alcoholicDrinkProperties.alcoholLevel
      );
    }
    if (result?.alcoholicDrinkProperties?.beerProperties?.degreeOfBalling) {
      result.alcoholicDrinkProperties.beerProperties.degreeOfBalling =
        convertDecimalToPercent(
          result.alcoholicDrinkProperties.beerProperties.degreeOfBalling
        );
    }
  } else {
    if (result?.alcoholicDrinkProperties?.alcoholLevel) {
      result.alcoholicDrinkProperties.alcoholLevel = convertPercentToDecimal(
        result.alcoholicDrinkProperties.alcoholLevel
      );
    }
    if (result?.alcoholicDrinkProperties?.beerProperties?.degreeOfBalling) {
      result.alcoholicDrinkProperties.beerProperties.degreeOfBalling =
        convertPercentToDecimal(
          result.alcoholicDrinkProperties.beerProperties.degreeOfBalling
        );
    }
  }

  return result;
}

/// convert a primary product sheet to a secondary product sheet
export function asSecondaryProductSheet(
  data: ProductDetailedResponse
): ProductDetailedResponse {
  return {
    ...data,
    ...(data.alcoholicDrinkProperties && {
      alcoholicDrinkProperties: {
        ...data.alcoholicDrinkProperties,
        ...(data.alcoholicDrinkProperties.beerProperties && {
          beerProperties: {
            ...data.alcoholicDrinkProperties.beerProperties,
            degreeOfBalling: undefined,
          },
        }),
        ...(data.alcoholicDrinkProperties.grapeWineProperties && {
          grapeWineProperties: {
            ...data.alcoholicDrinkProperties.grapeWineProperties,
            characters: undefined,
          },
        }),
        ...(data.alcoholicDrinkProperties.spiritProperties && {
          spiritProperties: {
            ...data.alcoholicDrinkProperties.spiritProperties,
            ...(data.alcoholicDrinkProperties.spiritProperties
              .whiskyProperties && {
              whiskyProperties: {
                ...data.alcoholicDrinkProperties.spiritProperties
                  .whiskyProperties,
                barrelType: undefined,
              },
            }),
            ...(data.alcoholicDrinkProperties.spiritProperties
              .cognacProperties && {
              cognacProperties: {
                ...data.alcoholicDrinkProperties.spiritProperties
                  .cognacProperties,
                barrelType: undefined,
              },
            }),
            ...(data.alcoholicDrinkProperties.spiritProperties
              .fruitSpiritProperties && {
              fruitSpiritProperties: {
                ...data.alcoholicDrinkProperties.spiritProperties
                  .fruitSpiritProperties,
                brewingProcess: undefined,
                agingMethod: undefined,
              },
            }),
          },
        }),
      },
    }),
    detailedDescription: undefined,
    videoLink: undefined,
    // when implementing productImages, it is important that
    // only one image is allowed for secondary product sheets,
    // the rest must be removed here
    secondaryProductSheet: true,
  };
}
